export const EDITOR_BOTTOM_TOOLBAR_BOTTOM_OFFSET_PX = 24;
export const EDITOR_BOTTOM_TOOLBAR_HEIGHT_PX = 56;
export const EDITOR_BOTTOM_TOAST_GAP_PX = 16;
export const EDITOR_BOTTOM_TOAST_OFFSET_PX =
  EDITOR_BOTTOM_TOOLBAR_BOTTOM_OFFSET_PX +
  EDITOR_BOTTOM_TOOLBAR_HEIGHT_PX +
  EDITOR_BOTTOM_TOAST_GAP_PX;

export const resolveEditorViewportCenterLeft = (unityLeftPad: string): string =>
  `calc(${unityLeftPad} + (100vw - ${unityLeftPad}) / 2)`;

export type EditorSaveToastContext =
  | "default"
  | "addSpace"
  | "duplicateSpace"
  | "spaceSwitch"
  | "spaceSwitchModal";

interface PendingEditorSaveToastState {
  context: EditorSaveToastContext;
  suppressToast: boolean;
  requestedAt: number;
}

interface ConsumedEditorSaveToastState {
  context: EditorSaveToastContext;
  suppressToast: boolean;
}

const DEFAULT_EDITOR_SAVE_TOAST_STATE: ConsumedEditorSaveToastState = {
  context: "default",
  suppressToast: false,
};

const PENDING_EDITOR_SAVE_TOAST_TTL_MS = 5 * 60 * 1000;

declare global {
  interface Window {
    __pendingEditorSaveToastQueue?: PendingEditorSaveToastState[];
  }
}

const hasWindow = (): boolean => typeof window !== "undefined";

const isFreshPendingState = (state?: PendingEditorSaveToastState): boolean => {
  if (!state) return false;
  return Date.now() - state.requestedAt <= PENDING_EDITOR_SAVE_TOAST_TTL_MS;
};

export const markPendingEditorSaveToast = (
  context: EditorSaveToastContext,
  suppressToast = false,
): void => {
  if (!hasWindow()) return;
  if (!window.__pendingEditorSaveToastQueue) {
    window.__pendingEditorSaveToastQueue = [];
  }
  window.__pendingEditorSaveToastQueue.push({
    context,
    suppressToast,
    requestedAt: Date.now(),
  });
};

export const clearPendingEditorSaveToast = (): void => {
  if (!hasWindow()) return;
  delete window.__pendingEditorSaveToastQueue;
};

export const consumePendingEditorSaveToast =
  (): ConsumedEditorSaveToastState => {
    if (!hasWindow()) return DEFAULT_EDITOR_SAVE_TOAST_STATE;

    const pendingQueue = window.__pendingEditorSaveToastQueue || [];
    if (pendingQueue.length === 0) {
      return DEFAULT_EDITOR_SAVE_TOAST_STATE;
    }

    // FIFO consume: saveJson 요청 순서와 동일한 context를 매칭
    const pendingState = pendingQueue.shift();
    if (pendingQueue.length === 0) {
      delete window.__pendingEditorSaveToastQueue;
    } else {
      window.__pendingEditorSaveToastQueue = pendingQueue;
    }

    if (!isFreshPendingState(pendingState)) {
      return DEFAULT_EDITOR_SAVE_TOAST_STATE;
    }

    return {
      context: pendingState.context,
      suppressToast: pendingState.suppressToast,
    };
  };

/**
 * Editor Toast layer.
 *
 * MUI `Dialog`/`Modal`의 기본 layer는 1300이다. Toast를 그 위에 두면 Toast가
 * 열려 있는 동안 겹치는 좌표의 modal 조작을 Toast surface가 가져간다. Editor
 * Toast는 Unity viewport 중앙·bottom toolbar 바로 위에 놓이고 Import/Replace
 * modal의 footer CTA도 같은 영역이라 실제로 겹친다.
 *
 * 그래서 modal 아래(drawer layer와 같은 1200)에 둔다. 일반 화면 콘텐츠 위에는
 * 그대로 뜨고, 열려 있는 dialog의 조작만 가리지 않는다.
 */
export const EDITOR_TOAST_Z_INDEX = 1200;
